import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { resolveActionKeysToRegex } from './action-key-resolver.js';
import { getAuditLogPaged, getAuditLogExport } from 'services/audit-logs-service';
import { getCompaniesPaged } from 'services/company-service';
import { useForm } from 'react-hook-form';
import { Dialog } from 'Components/ui';
import { useLocaleFormatting } from 'hooks/useLocaleFormatting';
import { formatDateOnlyWithTimezone, formatTimeOnlyWithTimezone } from 'utils/date_utils';
import { resolveResultWithDetail } from './result-label.js';

// Shared data/query layer for both Tenant/index.jsx and Global/index.jsx — extracted from the
// single AuditLogs/index.jsx that used to serve both. `withCompanyFilter` is the ONLY thing
// that differs between the two callers: it turns on the targetCompanyKey form field/query
// param/export param and the allCompanies lookup query. Everything else (date/type/service/
// actorType filters, search, pagination, CSV export) is identical for both pages.
export const useAuditLogQuery = ({ withCompanyFilter = false } = {}) => {
  const selectedTenant = useSelector(useAuthTenant);
  const { t, i18n } = useTranslation('audit');
  const { companyTimezone, companyLocale } = useLocaleFormatting();
  const [refetch, setRefetch] = useState(false);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  // The row whose AuditLogDetailDialog is open (null = closed). Holds the already-loaded list
  // row itself — the list response carries every field the dialog shows, so no extra fetch.
  const [selectedLog, setSelectedLog] = useState(null);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      typeFilter: '',
      searchContent: '',
      startDate: '',
      endDate: '',
      service: '',
      actorType: '',
      // Defaults to the viewing VO staff member's own company — never blank — so the Global
      // page never needs a generic "my company" placeholder option (see Global/index.jsx).
      ...(withCompanyFilter ? { targetCompanyKey: selectedTenant?.slug ?? '' } : {}),
    },
  });

  const { data: allCompanies } = useQuery(
    ['getCompaniesForAuditLogFilter'],
    async () => {
      const result = await getCompaniesPaged(1, 1000);
      return result?.data?.data ?? [];
    },
    { enabled: withCompanyFilter }
  );

  const getContentSearch = () => {
    if (watch('typeFilter') === '') return '';
    const raw = watch('searchContent');
    if (!raw) return '';

    if (watch('typeFilter') === 'Ação') {
      const regex = resolveActionKeysToRegex(i18n, raw);
      if (regex) return regex;
    }

    return raw;
  };

  const getValueTypeFilter = () => {
    switch (watch('typeFilter')) {
      case 'Usuário':
        return 'user';
      case 'Ação':
        return 'action';
      case 'Resposta':
        return 'return';
      default:
        return '';
    }
  };

  const getValueStartDateFilter = () => {
    const v = getValues('startDate');
    return (v == null || v === '') ? null : v;
  };

  const getValueEndDateFilter = () => {
    const v = getValues('endDate');
    return (v == null || v === '') ? null : v;
  };

  const getValueServiceFilter = () => {
    const v = getValues('service');
    return (v == null || v === '') ? null : v;
  };

  const getValueActorTypeFilter = () => {
    const v = getValues('actorType');
    return (v == null || v === '') ? null : v;
  };

  const getValueTargetCompanyKeyFilter = () => {
    if (!withCompanyFilter) return null;
    const v = getValues('targetCompanyKey');
    return (v == null || v === '') ? null : v;
  };

  const {
    isLoading,
    error,
    data: pagedLogs,
  } = useQuery(['getLogs', refetch, page, companyTimezone], async () => {
    if (selectedTenant.slug === undefined) return [];
    const search = getContentSearch();
    const filterValue = getValueTypeFilter();
    const filterStartDate = getValueStartDateFilter();
    const filterEndDate = getValueEndDateFilter();
    const filterService = getValueServiceFilter();
    const filterActorType = getValueActorTypeFilter();
    const filterTargetCompanyKey = getValueTargetCompanyKeyFilter();
    const result = await getAuditLogPaged(
      selectedTenant.slug,
      page,
      15,
      filterValue,
      search,
      filterStartDate,
      filterEndDate,
      filterService,
      filterActorType,
      filterTargetCompanyKey,
      companyTimezone,
    );
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  // FR-8/FR-9: ported from Policies/Show/Accepts's handleExportCsv (headers array, row-map,
  // Blob, anchor-download) — the confirmed porting source. Queries the new /export endpoint
  // fresh with the page's CURRENT filter state, not the already-loaded paged rows, so the
  // export always reflects exactly what the user has filtered by, per FR-9.
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const search = getContentSearch();
      const filterValue = getValueTypeFilter();
      const filterStartDate = getValueStartDateFilter();
      const filterEndDate = getValueEndDateFilter();
      const filterService = getValueServiceFilter();
      const filterActorType = getValueActorTypeFilter();
      const filterTargetCompanyKey = getValueTargetCompanyKeyFilter();
      const result = await getAuditLogExport(
        selectedTenant.slug,
        filterValue,
        search,
        filterStartDate,
        filterEndDate,
        filterService,
        filterActorType,
        filterTargetCompanyKey,
        companyTimezone,
      );

      if (result?.data?.exceededCap) {
        Dialog.alert({
          title: 'Exportação muito grande',
          message: `Encontramos ${result.data.totalDocs} registros para os filtros atuais, acima do limite de exportação. Restrinja os filtros (por exemplo, um período menor) e tente novamente.`,
          icon: 'error',
        });
        return;
      }

      const rows = result?.data?.data ?? [];
      if (rows.length === 0) {
        Dialog.alert({
          title: 'Nada para exportar',
          message: 'Não há logs para os filtros atuais.',
          icon: 'error',
        });
        return;
      }

      const headers = ['Data', 'Hora', 'Usuário', 'Ação', 'Resposta', 'Serviço', 'Tipo de ator'];
      const csvRows = rows.map(item => [
        `"${formatDateOnlyWithTimezone(item.displayTimestamp, companyTimezone, companyLocale)}"`,
        `"${formatTimeOnlyWithTimezone(item.displayTimestamp, companyTimezone, companyLocale)}"`,
        `"${item.user ?? ''}"`,
        `"${item.action ?? ''}"`,
        `"${resolveResultWithDetail(t, item).replace(/"/g, '""')}"`,
        `"${item.service ?? ''}"`,
        `"${item.actorType ?? ''}"`,
      ]);
      const csv = [headers, ...csvRows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${selectedTenant.slug}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    t,
    i18n,
    selectedTenant,
    companyTimezone,
    companyLocale,
    allCompanies,
    refetch,
    setRefetch,
    page,
    setPage,
    isExporting,
    selectedLog,
    setSelectedLog,
    register,
    control,
    reset,
    watch,
    getValues,
    isLoading,
    error,
    pagedLogs,
    handleExportCsv,
  };
};
